import LedgerScannerClient from '@plug-core/clients/LedgerScannerClient'
import useBalances from '@plug-core/hooks/useBalances'
import useConfig from '@plug-core/hooks/useConfig'
import useWallet from '@plug-core/hooks/useWallet'
import { logger } from '@plug-core/logger'
import { useBalanceChanges } from '@plug-core/state'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { Chip, Text } from '@ui'
import React, { useEffect } from 'react'

interface BalanceChangeProps {
  totalUsdBalance: number
}

const BalanceChange: React.FC<BalanceChangeProps> = ({ totalUsdBalance }) => {
  const { wallet } = useWallet()
  const { principalId, accountId } = wallet
  const { tokens } = useConfig()
  const { walletTotalBalanceChange, setWalletBalanceChange } = useBalanceChanges()
  const { filteredBalances } = useBalances()

  const balanceChange = walletTotalBalanceChange[principalId]

  const tokenIds = filteredBalances.map((tb) => tb.token.id)

  const fetchPastBalances = async () => {
    const relevantTokens = tokens.filter((t) => tokenIds.includes(t.id)).map((t) => t.id)

    try {
      const change = await LedgerScannerClient.fetchBalanceChange(wallet, relevantTokens)

      setWalletBalanceChange(principalId, change)
    } catch (e) {
      setWalletBalanceChange(principalId, undefined)

      logger.error('Failed to fetch balance change', { originalError: e })
    }
  }

  useEffect(() => {
    fetchPastBalances()

    const interval = setInterval(fetchPastBalances, 60 * 60 * 1000)

    return () => clearInterval(interval)
  }, [principalId, accountId, JSON.stringify(tokenIds)])

  if (!balanceChange) {
    return null
  } else {
    const usd = balanceChange
    const percentage = (usd / totalUsdBalance) * 100

    if (usd === 0) return null

    const up = percentage > 0

    return (
      <div className="flex items-center gap-1.5">
        <Text as="span" variant="body-s" weight="medium" tone={up ? 'success' : 'danger'} tabular>
          {formatMoney(usd, undefined, { signDisplay: 'exceptZero' })}
        </Text>
        <Chip tone={up ? 'success' : 'danger'} className="tabular font-semibold">
          {formatNumber(percentage, '%', {
            signDisplay: 'exceptZero',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Chip>
      </div>
    )
  }
}

export default BalanceChange
